<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { CDN, MARQUEE_ICONS } from './constants'
import { prefersReducedMotion } from '../../utils/environment'

const listRef = ref<HTMLElement | null>(null)

const icons = [...MARQUEE_ICONS]
const iconSrc = (name: string) => `${CDN}/${name}`

let rafId = 0
let halfWidth = 0
let offset = 0
let parallaxAccum = 0
let lastScrollY = 0

const wave = {
  amplitude: 33,
  frequency: 0.18,
  speed: 0.9,
  scrollSpeed: 0.6,
  parallax: 0.3
}

const calcHalfWidth = () => {
  const list = listRef.value
  if (!list) return
  const items = list.querySelectorAll<HTMLElement>('.icon-item')
  const half = items.length / 2
  if (half > 0 && half < items.length) {
    halfWidth = items[half].offsetLeft
  } else {
    halfWidth = list.scrollWidth / 2
  }
}

const onScroll = () => {
  const sy = window.pageYOffset || 0
  parallaxAccum += (sy - lastScrollY) * wave.parallax
  lastScrollY = sy
}

const onResize = () => {
  calcHalfWidth()
}

const animate = (time: number) => {
  rafId = requestAnimationFrame(animate)
  const list = listRef.value
  if (!list) return
  if (halfWidth <= 0) {
    calcHalfWidth()
    return
  }

  const t = time * 0.001
  offset += wave.scrollSpeed
  const x = -(offset + parallaxAccum)
  const mod = ((x % halfWidth) + halfWidth) % halfWidth
  list.style.transform = `translateX(${(-mod).toFixed(2)}px)`

  const items = list.querySelectorAll<HTMLElement>('.icon-item')
  items.forEach((item, i) => {
    const y = Math.sin(t * wave.speed + i * wave.frequency) * wave.amplitude
    item.style.transform = `translateY(${y.toFixed(1)}px)`
  })
}

onMounted(() => {
  if (prefersReducedMotion()) return
  lastScrollY = window.pageYOffset || 0
  window.setTimeout(calcHalfWidth, 100)
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onResize, { passive: true })
  rafId = requestAnimationFrame(animate)
})

onBeforeUnmount(() => {
  cancelAnimationFrame(rafId)
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', onResize)
})
</script>

<template>
  <section class="icon-marquee" aria-hidden="true">
    <ul ref="listRef" class="icon-list">
      <li v-for="(icon, idx) in icons" :key="'icon-' + idx" class="icon-item">
        <img :src="iconSrc(icon)" alt="">
      </li>
      <li v-for="(icon, idx) in icons" :key="'icon-clone-' + idx" class="icon-item">
        <img :src="iconSrc(icon)" alt="">
      </li>
    </ul>
  </section>
</template>

<style scoped lang="scss">
.icon-marquee {
  position: relative;
  padding: 40px 0;
  overflow: hidden;
  background: #fff;
}

.icon-marquee::before,
.icon-marquee::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 160px;
  z-index: 2;
  pointer-events: none;
}

.icon-marquee::before {
  left: 0;
  background: linear-gradient(to right, #fff, transparent);
}

.icon-marquee::after {
  right: 0;
  background: linear-gradient(to left, #fff, transparent);
}

.icon-list {
  display: flex;
  gap: 50px;
  width: max-content;
  margin: 0;
  padding: 0;
  list-style: none;
  will-change: transform;
}

.icon-item {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: #fafafa;
}

.icon-item img {
  width: 32px;
  height: 32px;
  opacity: 1;
}
</style>
